import axiosClient from 'api/axiosClient';
import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import ViewAddInvestorGroupPack from 'features/ManageGroup/components/ViewAddInvestorGroupPack';
import { AppDialogHelper } from 'general/components/AppDialog';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTBSCard from 'general/components/OtherKeenComponents/KTBSCard';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppResource from 'general/constants/AppResource';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const defaultColumnIds = ['PACKAGE_NAME', 'DESCRIPTION', 'AMOUNT'];

function InvestorGroupPackListScreen(props) {
  // MARK: ---- params ----
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useSelector((state) => state.auth);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'PACKAGE_NAME',
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Tên gói')}
          </span>
        ),
        selector: (row) => row?.name,
        minWidth: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'DESCRIPTION',
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Mô tả')}
          </span>
        ),
        selector: (row) => row?.description,
        minWidth: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AMOUNT',
        name: (
          <div className="w-100 d-flex flex-column align-items-end">
            <div
              className="letter-spacing-unset"
              style={{
                fontSize: 12,
                color: AppColors.gray[900],
              }}
            >
              Giá bán
            </div>
            <div
              className="letter-spacing-unset font-weight-bold"
              style={{ color: AppColors.gray[700] }}
            >
              VNĐ
            </div>
          </div>
        ),
        selector: (row) => Utils.formatNumber(row?.amount ?? 0),
        width: '160px',
        style: tableStyle.columns.normal,
        right: true,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={{
              fontSize: 12,
              color: AppColors.gray[900],
            }}
          >
            {t('Thao tác')}
          </span>
        ),
        width: '100px',
        wrap: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        selector: (row) => {
          return (
            <div className="d-flex gap-2">
              <a
                href="#"
                className="btn btn-sm btn-clean btn-icon"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddNewInvestorGroupPack(row);
                }}
              >
                <i className="fa-regular fa-pen-to-square" style={{ color: AppColors.gray[700] }} />
              </a>

              <a
                href="#"
                className="btn btn-sm btn-clean btn-icon"
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete(row);
                }}
              >
                <i className="fa-regular fa-trash-can" style={{ color: AppColors.gray[700] }} />
              </a>
            </div>
          );
        },
      },
    ];
  }, []);
  const [filter, setFilter] = useState({
    page: 0,
    limit: AppConfigs.defaultPageLimit,
    q: '',
  });
  const [loading, setLoading] = useState(false);
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  useEffect(() => {
    const getInvestmentPackageList = async (params) => {
      setLoading(true);
      const res = await axiosClient.get('/investment/package', { params });
      setLoading(false);
      const { result, packages, total } = res;
      if (result === 'success') {
        setItems(packages);
        setTotal(total ?? packages?.length);
      }
    };

    getInvestmentPackageList(filter);
  }, [filter]);

  // MARK: ---- methods ----
  const handleAddNewInvestorGroupPack = (row) => {
    AppCustomDialogHelper.show(
      _.isUndefined(row) ? 'Thêm gói đầu tư' : 'Cập nhật gói đầu tư',
      <ViewAddInvestorGroupPack
        item={row}
        onClosed={() => {
          AppCustomDialogHelper.hide();
        }}
        onSubmitted={async (values) => {
          const params = {
            name: values?.name,
            description: values?.description,
            amount: values?.price,
          };
          if (_.isUndefined(row)) {
            const res = await axiosClient.post('/investment/package/create', params);
            const { result, reason } = res;
            if (result === 'success') {
              ToastHelper.showSuccess('Thêm gói đầu tư nhóm thành công');
              AppCustomDialogHelper.hide();
              setFilter({ ...filter });
            } else {
              ToastHelper.showError(reason ?? 'Thêm gói đầu tư nhóm thất bại');
            }
          } else {
            const res = await axiosClient.put(`/investment/package/${row?.packageId}`, params);
            const { result, reason } = res;
            if (result === 'success') {
              ToastHelper.showSuccess('Cập nhật gói đầu tư nhóm thành công');
              AppCustomDialogHelper.hide();
              setFilter({ ...filter });
            } else {
              ToastHelper.showError(reason ?? 'Cập nhật gói đầu tư nhóm thất bại');
            }
          }
        }}
      />
    );
  };

  const handleDelete = (row) => {
    AppDialogHelper.show(
      'Xoá gói đầu tư?',
      `Bạn thực sự muốn xoá gói đầu tư ${row?.name} ?`,
      [
        {
          title: 'Hủy bỏ',
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
          },
        },
        {
          title: 'Xoá',
          type: 'negative',
          onClick: async () => {
            AppDialogHelper.hide();
            const res = await axiosClient.delete(`/investment/package/${row?.packageId}`);
            const { result, reason } = res;
            if (result === 'success') {
              ToastHelper.showSuccess('Xoá thành công');
              setFilter({ ...filter });
            } else {
              ToastHelper.showError(reason ?? 'Xoá thất bại');
            }
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogDeleteVehicle
    );
  };

  return (
    <KT01BaseLayout isFullScreen={true}>
      <div className="container-fluid px-4 my-4">
        <KTBSCard
          borderRadius="10"
          title={t('Danh sách gói đầu tư')}
          description={
            <div>
              <span className="font-weight-bolder" style={{ color: AppColors.gray[800] }}>
                {t('total')}:{' '}
              </span>
              <span style={{ color: AppColors.primary }}>
                {total != 0 ? total : items.length} {t('Gói đầu tư').toLowerCase()}
              </span>
            </div>
          }
          additionalHeaderClassName="pt-4 pb-2"
          additionalBodyClassName="py-0"
          additionalFooterClassName="py-4"
          hideDividerHeader
          hideDividerFooter
          toolbar={
            <div className="d-flex gap-2">
              <KTBSButton
                color={KTBSButtonColor.success}
                title={t('Thêm gói đầu tư')}
                size={KTBSButtonSize.default}
                borderRadius={5}
                buttonTitleFontWeight="600"
                iconElement={<i className="fa-regular fa-circle-plus" />}
                onPress={() => {
                  handleAddNewInvestorGroupPack();
                }}
              />
            </div>
          }
          subToolbar={
            <div className="w-100 d-flex align-items-end flex-wrap">
              <div className="col-lg-3 col-sm-6 px-0 pr-sm-4 py-2">
                <label htmlFor="search" className="mb-0 mr-2 font-weight-bolder">
                  {t('search')}
                </label>
                <KTFormSearch
                  name="search-bar"
                  placeholder={t('Theo tên gói, mô tả...')}
                  value={filter.q}
                  onSubmit={(text) => {
                    setFilter({
                      ...filter,
                      q: text,
                    });
                  }}
                />
              </div>
              {/* cau hinh cot */}
              <div className="pr-0 py-2">
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                />
              </div>
            </div>
          }
          footer={
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={items.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                setFilter({
                  ...filter,
                  page: iNewPage - 1,
                });
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                setFilter({
                  ...filter,
                  limit: iNewRowsPerPage,
                  page: 0,
                });
              }}
              loading={loading}
              color={KTPaginationColor.success}
              appendText="gói đầu tư HTKD"
            />
          }
          body={
            <KTTable
              columns={columns}
              items={items}
              selectableRows={false}
              striped={false}
              compactGrid={false}
              onClickedRow={(row) => {
                handleAddNewInvestorGroupPack(row);
              }}
              onSelectedRowsChanged={(rows) => { }}
              onDoubleClickedRow={(row) => { }}
              noDataComponent={
                <EmptyView
                  icon={imgEmptyNoData}
                  title="Không có dữ liệu để hiển thị"
                  text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                />
              }
            />
          }
        />
      </div>
    </KT01BaseLayout>
  );
}

export default InvestorGroupPackListScreen;
